import {
  RECOVERY_PROFILE_ID_KEY,
  WORKING_CAPITAL_PROFILE_ID_KEY,
  BUSINESS_WORKING_CAPITAL_PROFILE_ID_KEY,
  BUSINESS_ANONYMOUS_WORKING_CAPITAL_PROFILE_ID_KEY,
  PROPERTY_PROFILE_ID_KEY,
  GRANTS_PROFILE_ID_KEY,
  LOANS_PROFILE_ID_KEY,
  ALL_PRODUCTS_PROFILE_ID_KEY,
  CREDIT_IMPROVEMENT_PROFILE_ID_KEY,
  RD_TAX_CLAIMS_PROFILE_ID_KEY,
  BUSINESS_CREDIT_IMPROVEMENT_PROFILE_ID_KEY,
  BUSINESS_CREDIT_REVIEW_PROFILE_ID_KEY,
  BUSINESS_CREDIT_REVIEW_BY_INTRODUCER_PROFILE_ID_KEY,
  BUSINESS_PROPERTY_PROFILE_ID_KEY
} from 'constants';
import { QuestionnaireType } from 'enums/questionnaire-type';
import { QuestionnaireModal } from '@capitalise/ui-questionnaire-alpha';
import { withRouter, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQueryGetProfileDetails } from 'embed/services/queries';
import { useQueryGetProfileByEntityId } from 'services/case/queries';

const MOUNT_NODE = document.getElementById('app');

// Note: update this redirect link to be handled by the ui-schema
const options = {
  [QuestionnaireType.introducerWorkingCapital]: {
    profileKey: WORKING_CAPITAL_PROFILE_ID_KEY,
    redirect: (id) => `/funding/search/${id}/profile?openModal=pre_profile_alpha`,
  },
  [QuestionnaireType.introducerRecovery]: {
    profileKey: RECOVERY_PROFILE_ID_KEY,
    redirect: ({ id }) => `/cases/${id}`,
  },
  [QuestionnaireType.introducerProperty]: {
    profileKey: PROPERTY_PROFILE_ID_KEY,
    redirect: (id) => `/funding/search/${id}/profile?openModal=pre_profile_alpha`,
  },
  [QuestionnaireType.introducerGrants]: {
    profileKey: GRANTS_PROFILE_ID_KEY,
    redirect: ({ id }) => `/cases/${id}`,
  },
  [QuestionnaireType.introducerRecoveryLoanScheme]: {
    profileKey: LOANS_PROFILE_ID_KEY,
    redirect: (id) => `/funding/search/${id}/profile?openModal=pre_profile_alpha`,
  },
  [QuestionnaireType.introducerAllFundingProducts]: {
    profileKey: ALL_PRODUCTS_PROFILE_ID_KEY,
    redirect: (id) => `/funding/search/${id}/profile?openModal=pre_profile_alpha`,
  },
  [QuestionnaireType.introducerCreditImprovement]: {
    profileKey: CREDIT_IMPROVEMENT_PROFILE_ID_KEY,
    redirect: ({ id }) => `/cases/${id}`,
  },
  [QuestionnaireType.introducerRdTaxClaim]: {
    profileKey: RD_TAX_CLAIMS_PROFILE_ID_KEY,
    redirect: ({ id }) => `/cases/${id}`,
  },
  [QuestionnaireType.businessWorkingCapital]: {
    profileKey: BUSINESS_WORKING_CAPITAL_PROFILE_ID_KEY,
    redirect: (id) => `/business/funding/search/${id}/profile?openModal=pre_profile_alpha`,
  },
  [QuestionnaireType.businessAnonymousWorkingCapital]: {
    profileKey: BUSINESS_ANONYMOUS_WORKING_CAPITAL_PROFILE_ID_KEY,
    redirect: (id) => `/business/funding/search/${id}/profile`,
  },
  [QuestionnaireType.businessCreditImprovement]: {
    profileKey: BUSINESS_CREDIT_IMPROVEMENT_PROFILE_ID_KEY,
    redirect: () => `/business/applications`,
  },
  [QuestionnaireType.businessCreditReview]: {
    profileKey: BUSINESS_CREDIT_REVIEW_PROFILE_ID_KEY,
    redirect: () => `/business/applications`,
  },
  [QuestionnaireType.businessCreditReviewByIntroducer]: {
    profileKey: BUSINESS_CREDIT_REVIEW_BY_INTRODUCER_PROFILE_ID_KEY,
    redirect: (id, entityId) => `/accountant/success?profile=${id}&entityId=${entityId}`,
  },
  [QuestionnaireType.businessProperty]: {
    profileKey: BUSINESS_PROPERTY_PROFILE_ID_KEY,
    redirect: () => `/business/applications`,
  },
};

const Questionnaire = (props) => {
  const {
    closeModal,
    history,
    isOpen,
    type,
    onApplyMatches,
    entityId,
    ignoreStorageId,
    profileLoadedCallback,
    forceNewProfile,
    ...rest
  } = props;
  
  const { profileKey, redirect } = options?.[type] || {
    profileKey: '',
    redirect: () => `/dashboard`,
  };

  const { data: profile, isFetched } = useQueryGetProfileByEntityId(!forceNewProfile && entityId);

  const existingProfileId = profile?.uuid;

  const defaultProfileId = localStorage.getItem(profileKey);
  const [profileId, setProfileId] = useState(existingProfileId || defaultProfileId);

  const setProfileIdOnStorage = (id) => {
    setProfileId(id);

    if (id) {
      localStorage.setItem(profileKey, id);

      return;
    }

    localStorage.removeItem(profileKey);
  };

  const handleCloseModal = () => {
    closeModal();
    history.replace({ search: history.location.search });
  };

  const onApplySuccess = (id) => {
    setProfileIdOnStorage(null);
    closeModal();
    if (onApplyMatches) {
      onApplyMatches(redirect(id, entityId));
    } else {
      history.push(redirect(id, entityId));
    }
  };

  useEffect(() => {
    if (!ignoreStorageId) {
      setProfileId(defaultProfileId);
    }
  }, [defaultProfileId]);

  useEffect(() => {
    if (ignoreStorageId) {
      setProfileId(profile?.uuid);
    }
  }, [profile?.uuid]);

  useEffect(() => {
    if (isFetched) {
      profileLoadedCallback?.();
    }
  }, [isFetched]);

  return ReactDOM.createPortal(
    <QuestionnaireModal
      bodyOpenClassName="o-overflow-hidden"
      closeModal={handleCloseModal}
      environment={window._env_.RUNNING_ENV}
      isOpen={isOpen}
      onApplySuccess={onApplySuccess}
      onProfileIdChange={setProfileIdOnStorage}
      options={{
        type,
        ...(!profileId ? {} : { profileId }),
        ...rest,
        hasNotIntercom: true,
        ...(!entityId ? {} : { entityId }),
      }}
    />,
    MOUNT_NODE,
  );
};

Questionnaire.propTypes = {
  apiKey: PropTypes.string,
  closeModal: PropTypes.func.isRequired,
  group: PropTypes.string,
  history: PropTypes.object,
  isOpen: PropTypes.bool,
  match: PropTypes.object,
  showMatches: PropTypes.bool,
  step: PropTypes.string,
  type: PropTypes.string,
  onApplyMatches: PropTypes.func,
  referralCode: PropTypes.string,
  forceNewProfile: PropTypes.bool,
};

Questionnaire.defaultProps = {
  forceNewProfile: false,
};

export default withRouter(Questionnaire);
